import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import submitContactUsRequest from './ContactUsService';
import './ContactUsPage.scss';

const CONTACT_REASONS = [
  'Learn how Effiez can simplify your workforce management',
  'Get help with compliance, licences, and certifications',
  'Manage employee availability and leave with ease',
  'Streamline rostering and job allocations',
  'Receive support from real local people',
];

export default function ContactUsPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [orderDetails, setOrderDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!name.trim() || !phoneNumber.trim()) {
      setStatus({ type: 'error', text: 'Please enter both your name and phone number.' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', text: '' });

    try {
      await submitContactUsRequest({
        name: name.trim(),
        phoneNumber: phoneNumber.trim(),
        orderDetails: orderDetails.trim(),
      });

      setStatus({ type: 'success', text: 'Request submitted. Our team will contact you shortly.' });
      setName('');
      setPhoneNumber('');
      setOrderDetails('');
      if (window.fbq) {
        window.fbq('track', 'Lead', { value: 1 });
      }
    } catch (error) {
      setStatus({ type: 'error', text: 'Unable to submit your request right now. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-us-page">
      <div className="contact-us-glow contact-us-glow-left" />
      <div className="contact-us-glow contact-us-glow-right" />

      <main className="contact-us-shell">
        <div className="contact-us-top-actions">
          <button type="button" className="contact-us-top-button contact-us-top-button-secondary" onClick={() => navigate('/about-us')}>
            About Us
          </button>
          <button type="button" className="contact-us-top-button" onClick={() => navigate('/home')}>
            Home
          </button>
        </div>

        <section className="contact-us-hero">
          <span className="contact-us-kicker">Contact Us</span>
          <h1>We&apos;re here to help.</h1>
          <p>
            Whether you have questions about Effiez, want to book a demo, need support, or simply want to learn how Effiez can help your trade business become more efficient, we&apos;d love to hear from you.
          </p>
          <p>
            At Effiez, you&apos;ll always deal with real people who understand the challenges faced by tradies and trade businesses. We believe great software should be backed by great support, and our team is ready to assist whenever you need us.
          </p>
        </section>

        <section className="contact-us-layout">
          <article className="contact-us-content">
            <h2>Get in Touch</h2>
            <div className="contact-us-details">
              <div>
                <h3>Phone</h3>
                <p>0413 909 280</p>
              </div>
              <div>
                <h3>Email</h3>
                <p>
                  <a href="mailto:effiez.campaign@gmail.com">effiez.campaign@gmail.com</a>
                </p>
              </div>
            </div>

            <h2>Why Contact Effiez?</h2>
            <ul className="contact-us-reasons">
              {CONTACT_REASONS.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>

            <p className="contact-us-summary">
              No call centres. No automated responses. Just genuine support from a team that cares about helping tradies succeed.
            </p>
            <p className="contact-us-summary">
              Contact us today and discover how Effiez can make running your business easier.
            </p>
          </article>

          <form className="contact-us-card" onSubmit={handleSubmit}>
            <h2 className="contact-us-title">Contact Effiez</h2>
            <p className="contact-us-hint">Enter your details and submit to request a call back.</p>

            <div className="contact-us-field">
              <label htmlFor="contact-us-name">Name</label>
              <input
                id="contact-us-name"
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Your full name"
              />
            </div>

            <div className="contact-us-field">
              <label htmlFor="contact-us-phone">Phone number</label>
              <input
                id="contact-us-phone"
                type="tel"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
                placeholder="04xx xxx xxx"
              />
            </div>

            <div className="contact-us-field">
              <label htmlFor="contact-us-order-details">Your order details (optional)</label>
              <textarea
                id="contact-us-order-details"
                value={orderDetails}
                onChange={(event) => setOrderDetails(event.target.value)}
                placeholder="Describe how we can help"
                rows={4}
              />
            </div>

            <button className="contact-us-submit" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {status.text && (
              <p className={`contact-us-message ${status.type}`}>
                {status.text}
              </p>
            )}
          </form>
        </section>
      </main>
    </div>
  );
}